import { useState, useEffect, useRef, useMemo } from 'react';

interface UseFileUploadSyncProps {
  files: any[] | undefined;
  uploadQueueFiles: any[];
  allowContinuousUpload: boolean;
  uploadQueue?: any;
  onChangeFiles?: (files: any[]) => void;
}

export const useFileUploadSync = ({
  files,
  uploadQueueFiles,
  allowContinuousUpload,
  uploadQueue,
  onChangeFiles
}: UseFileUploadSyncProps) => {
  const [listFileUpload, setListFileUpload] = useState<any[]>([]);
  const isInternalUpdate = useRef(false);
  const prevFilesRef = useRef<any[]>([]);

  // Sincronizar con la prop `files` del padre
  useEffect(() => {
  
    const filesChanged = files !== prevFilesRef.current && 
      (files?.length !== prevFilesRef.current?.length ||
       files?.some((f: any, i: number) => f !== prevFilesRef.current[i]));

    if (!filesChanged && prevFilesRef.current.length > 0) {
      return;
    }

    prevFilesRef.current = files || [];
    isInternalUpdate.current = true;

    if (files === undefined) {
      setListFileUpload([]);
    }
    else if (files && files.length > 0) {
      const saved = files.filter((item: any) => item.url || item.key);
      const tempFilesFromProp = files.filter((item: any) => item.isTemporary);
      const newFileObjects = files.filter((item: any) => 
        !item.url && !item.key && !item.isTemporary && item instanceof File
      );

      if (allowContinuousUpload) {
        if (tempFilesFromProp.length > 0) {
          setListFileUpload([...saved, ...tempFilesFromProp]);
        } else if (newFileObjects.length > 0) {
          setListFileUpload(prev => {
            const existingTempFiles = prev.filter((item: any) => 
              item.isTemporary && !item.url && !item.key
            );
            return [...saved, ...existingTempFiles, ...newFileObjects];
          });
        } else {
          // Refresh después de upload - remover temporales completados
          setListFileUpload(prev => {
            if (saved.length > 0) {
              const tempFilesStillUploading = prev.filter((item: any) => {
                if (!item.isTemporary) return false;
                
                if (item.queueId) {
                  const queueFile = uploadQueueFiles.find((qf: any) => qf.id === item.queueId);
                  const shouldKeep = queueFile && queueFile.status !== 'success';
                  return shouldKeep;
                }
                return false;
              });
              return [...saved, ...tempFilesStillUploading];
            }
            return prev;
          });
        }
      } else {
        if (uploadQueue && newFileObjects.length > 0) {
          setListFileUpload([...saved, ...newFileObjects]);
        } else {
          setListFileUpload(saved);
        }
      }
    } else if (files && files.length === 0) {
      setListFileUpload(prev => {
        const filesInQueue = prev.filter((item: any) => {
          if (!item.isTemporary) return false;
          
          if (item.queueId) {
            const inQueue = uploadQueueFiles.some((qf: any) => qf.id === item.queueId);
            return inQueue;
          }
          return false;
        });
        return filesInQueue;
      });
    }

    setTimeout(() => {
      isInternalUpdate.current = false;
    }, 100);

  }, [files, allowContinuousUpload, uploadQueueFiles.length]);

  // Notificar cambios al padre
  useEffect(() => {
    if (onChangeFiles && !isInternalUpdate.current) {
      onChangeFiles(listFileUpload);
    }
  }, [listFileUpload, onChangeFiles]);

  // Limpiar archivos exitosos de la cola después de mostrar archivos de BD
  useEffect(() => {
    if (!uploadQueue?.clearSuccessfulFiles) return;

    if (uploadQueue.isProcessing) return;
    
    const hasDbFiles = listFileUpload.some((f: any) => f.url || f.key);
    const hasSuccessInQueue = uploadQueueFiles.some((qf: any) => qf.status === 'success');
    
    if (hasDbFiles && hasSuccessInQueue) {
      const timer = setTimeout(() => {
        uploadQueue.clearSuccessfulFiles();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [listFileUpload, uploadQueueFiles, uploadQueue]);

  // Enriquecer archivos con estado de upload
  const enrichedFiles = useMemo(() => {
    return listFileUpload.map((file: any) => {
      // Si ya tiene url/key (BD), return as-is
      if (file.url || file.key) {
        return file;
      }
      
      // Si es temporal con queueId, buscar su estado
      if (file.isTemporary && file.queueId) {
        const queueFile = uploadQueueFiles.find((qf: any) => qf.id === file.queueId);
        if (queueFile) {
          return {
            ...file,
            uploadStatus: queueFile.status,
            errorMessage: queueFile.errorMessage,
            onRetry: queueFile.onRetry
          };
        }
        return file;
      }
      
      // Archivo File original sin procesar
      return file;
    });
  }, [listFileUpload, uploadQueueFiles]);

  return {
    enrichedFiles,
    listFileUpload,
    setListFileUpload
  };
};